import { Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';

import { BRAND_COLORS } from '../../../../constants/styles';
import styles from '../../styles.module.scss';
import { getUniqueValues } from '../../utils/filterUtils';

export type DonationStatus =
  | 'Complete'
  | 'Partial'
  | 'Failed'
  | 'DBL→SGL'
  | 'Pending'
  | 'Ineligible';
export type DonationType = 'Single' | 'Double';

const DONATION_STATUS_COLORS: Record<DonationStatus, string> = {
  Complete: BRAND_COLORS.secondary.warmBeige,
  'DBL→SGL': BRAND_COLORS.primary.deepNavy,
  Ineligible: BRAND_COLORS.primary.red,
  Partial: BRAND_COLORS.primary.royalBlue,
  Failed: '#ff4d4f',
  Pending: '#faad14',
};

const DONATION_TYPE_COLORS: Record<DonationType, string> = {
  Double: BRAND_COLORS.primary.red,
  Single: BRAND_COLORS.primary.royalBlue,
};

export interface IDonationHistoryRow {
  id: string;
  shortId: number;
  date: string;
  donorName: string;
  locationName: string;
  regionName: string;
  type: DonationType;
  approvedML: number;
  status: DonationStatus;
}

export function getDonationHistoryColumns(
  donations: IDonationHistoryRow[],
  periodStart?: Date,
  periodEnd?: Date
): ColumnsType<IDonationHistoryRow> {
  const uniqueLocations = getUniqueValues(donations, (d) => d.locationName);
  const uniqueRegions = getUniqueValues(donations, (d) => d.regionName);

  return [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 110,
      render: (date: string) => {
        const d = dayjs(date);
        const isOutOfRange =
          periodStart &&
          periodEnd &&
          (d.isBefore(dayjs(periodStart)) || d.isAfter(dayjs(periodEnd)));
        return (
          <div className={isOutOfRange ? styles.outOfRange : undefined}>
            <div>{d.format('MM/DD/YYYY')}</div>
            <div
              style={{
                fontSize: 11,
                color: isOutOfRange ? '#a7b4c2' : '#8c8c8c',
              }}
            >
              {d.format('h:mm A')}
            </div>
          </div>
        );
      },
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Donor',
      dataIndex: 'donorName',
      key: 'donorName',
      width: 100,
      sorter: (a, b) => a.donorName.localeCompare(b.donorName),
    },
    {
      title: 'Location',
      dataIndex: 'locationName',
      key: 'locationName',
      width: 140,
      sorter: (a, b) => a.locationName.localeCompare(b.locationName),
      filters: uniqueLocations.map((name) => ({ text: name, value: name })),
      onFilter: (value, record) => record.locationName === value,
    },
    {
      title: 'Region',
      dataIndex: 'regionName',
      key: 'regionName',
      width: 120,
      sorter: (a, b) => a.regionName.localeCompare(b.regionName),
      filters: uniqueRegions.map((name) => ({ text: name, value: name })),
      onFilter: (value, record) => record.regionName === value,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 80,
      render: (type: DonationType) => (
        <Tag
          color={
            type === 'Double'
              ? DONATION_TYPE_COLORS.Double
              : DONATION_TYPE_COLORS.Single
          }
        >
          {type}
        </Tag>
      ),
      sorter: (a, b) => a.type.localeCompare(b.type),
      filters: [
        { text: 'Single', value: 'Single' },
        { text: 'Double', value: 'Double' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: 'Approved ML',
      dataIndex: 'approvedML',
      key: 'approvedML',
      width: 110,
      render: (ml: number) => (ml > 0 ? `${ml}ml` : '-'),
      sorter: (a, b) => a.approvedML - b.approvedML,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: DonationStatus) => (
        <Tag color={DONATION_STATUS_COLORS[status] || '#999'}>{status}</Tag>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
      filters: [
        { text: 'Complete', value: 'Complete' },
        { text: 'DBL→SGL', value: 'DBL→SGL' },
        { text: 'Partial', value: 'Partial' },
        { text: 'Failed', value: 'Failed' },
        { text: 'Pending', value: 'Pending' },
        { text: 'Ineligible', value: 'Ineligible' },
      ],
      onFilter: (value, record) => record.status === value,
    },
  ];
}
